import { exists } from "./booleans";

export interface CreateUrlOptions<T> {
    pathname?: string;
    searchParams?: T;
    /** Flag to allow null or undefined values to be passed into search params */
    allowNull?: boolean;
}

/**
 * A wrapper to easily construct a URL object - see https://developer.mozilla.org/en-US/docs/Web/API/URL
 * @param base origin for new url - ex. "https://github.com"
 * @param options
 */
export const createUrl = <T extends object>(
    base: string,
    { pathname, searchParams, allowNull }: CreateUrlOptions<T> = {
        pathname: "/",
        searchParams: undefined,
        allowNull: false
    }
): URL => {
    let url = new URL(base);

    url.pathname = pathname.startsWith("/") ? pathname : "/" + pathname;

    if (exists(searchParams)) {
        for (const [key, value] of Object.entries(searchParams)) {
            if (value) {
                if (Array.isArray(value)) {
                    value.forEach((v) => url.searchParams.append(key, v));
                } else {
                    url.searchParams.append(key, value.toString());
                }
            } else if (allowNull) {
                url.searchParams.append(key, "null");
            }
        }
    }

    return url;
};

export const removeQueryParams = (
    pathname: string = window.location.pathname
) => {
    window.history.replaceState(null, "", pathname);
};

/**
 * Removes the hash from the URL
 */
export const removeHash = (): void => {
    history.pushState(
        "",
        document.title,
        window.location.pathname + window.location.search
    );
};

export interface ParsedLine {
    component: string;
    url: URL;
    queryParams?: string;
    line: number;
    column: number;
}

/**
 * Parses a line of error stack trace into a structured object.
 * @param entry - A string representing a line from the error stack trace.
 * @returns An object containing the component name, URL, line number, and column number.
 */
export const parseLine = (entry: string): ParsedLine | null => {
    const regex = /^([^@]+)@(.+?):(\d+):(\d+)$/;
    const match = entry.match(regex);
    if (!match) return null;

    const [, component, fullUrl, lineStr, columnStr] = match;

    const url = new URL(fullUrl);

    return {
        component,
        url,
        line: parseInt(lineStr, 10),
        column: parseInt(columnStr, 10)
    };
};
